import React, { useEffect, useState } from "react";
import { ShieldCheck, Star, CheckCircle2, TrendingUp, Package } from "lucide-react";
import { api } from "../lib/api";

const TIER_COLOR = {
  Excellent: "#1f9d55", Great: "#C9A24B", Good: "#E2661F", Fair: "#8a8a8a",
};
const BADGE_ICON = {
  quality: ShieldCheck, fair_price: TrendingUp, top_rated: Star,
  verified: CheckCircle2, in_stock: Package, protection: ShieldCheck,
};

export const ConfidenceScore = ({ handle }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!handle) return;
    api.get(`/products/${handle}/confidence`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [handle]);

  if (!data) return null;
  const color = TIER_COLOR[data.tier] || "#C9A24B";
  const circumference = 2 * Math.PI * 26;
  const offset = circumference - (data.score / 100) * circumference;

  return (
    <div className="mt-6 border border-black/10 bg-white p-4" data-testid="confidence-score">
      <div className="flex items-center gap-4">
        <div className="relative w-[68px] h-[68px] shrink-0">
          <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
            <circle cx="32" cy="32" r="26" fill="none" stroke="#eee" strokeWidth="6" />
            <circle cx="32" cy="32" r="26" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 1s ease" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-black font-display leading-none" data-testid="confidence-value">{data.score}</span>
            <span className="text-[8px] uppercase tracking-wider text-muted-foreground">/100</span>
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Product Confidence</p>
          <p className="text-lg font-black font-display" style={{ color }} data-testid="confidence-tier">{data.tier}</p>
          <p className="text-xs text-muted-foreground">
            {data.reviews.count > 0 ? `${data.reviews.average}★ from ${data.reviews.count} review${data.reviews.count > 1 ? "s" : ""}` : "New to TAVEN — be the first to review"}
          </p>
        </div>
      </div>

      {data.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="trust-badges">
          {data.badges.map((b) => {
            const Icon = BADGE_ICON[b.key] || ShieldCheck;
            return (
              <span key={b.key} className="inline-flex items-center gap-1 bg-taven-gray/70 text-[10px] font-bold uppercase tracking-wider px-2 py-1" data-testid={`badge-${b.key}`}>
                <Icon className="w-3 h-3 text-taven-gold" /> {b.label}
              </span>
            );
          })}
        </div>
      )}

      {data.reasons.length > 0 && (
        <ul className="mt-3 space-y-1">
          {data.reasons.map((r) => (
            <li key={r} className="flex items-center gap-1.5 text-xs text-muted-foreground"><CheckCircle2 className="w-3 h-3 text-taven-gold shrink-0" /> {r}</li>
          ))}
        </ul>
      )}
      <p className="text-[10px] text-muted-foreground/70 mt-3 leading-relaxed">{data.note}</p>
    </div>
  );
};

export default ConfidenceScore;
